import type { SystemType, PendulumParams, SimSettings } from './types.ts';
import { rigidStepVerlet, type RigidState } from './physics/rigid.ts';
import { elasticDerivatives } from './physics/elastic.ts';
import { nonlinearDerivatives } from './physics/nonlinear.ts';
import { sculptureDerivatives, sculptureNodes } from './physics/sculpture.ts';
import { resonantDerivatives, resonantNodes } from './physics/resonant.ts';
import { bob1Position, bob2Position } from './physics/positions.ts';

export type SimState = number[];

export interface StepResult {
  state: SimState;
  nodes: Array<{ x: number; y: number }>;
}

function pack8(
  t1: number, w1: number, r1: number, rd1: number,
  t2: number, w2: number, r2: number, rd2: number,
): number[] {
  return [t1, w1, r1, rd1, t2, w2, r2, rd2];
}

export function rk4Step(
  sa: number[], sb: number[],
  derivFn: (sa: number[], sb: number[]) => { da: number[]; db: number[] },
  dt: number,
): { sa: number[]; sb: number[] } {
  const k1 = derivFn(sa, sb);
  const sa2 = sa.map((v, i) => v + 0.5 * dt * k1.da[i]);
  const sb2 = sb.map((v, i) => v + 0.5 * dt * k1.db[i]);
  const k2 = derivFn(sa2, sb2);
  const sa3 = sa.map((v, i) => v + 0.5 * dt * k2.da[i]);
  const sb3 = sb.map((v, i) => v + 0.5 * dt * k2.db[i]);
  const k3 = derivFn(sa3, sb3);
  const sa4 = sa.map((v, i) => v + dt * k3.da[i]);
  const sb4 = sb.map((v, i) => v + dt * k3.db[i]);
  const k4 = derivFn(sa4, sb4);
  return {
    sa: sa.map((v, i) => v + (dt / 6) * (k1.da[i] + 2 * k2.da[i] + 2 * k3.da[i] + k4.da[i])),
    sb: sb.map((v, i) => v + (dt / 6) * (k1.db[i] + 2 * k2.db[i] + 2 * k3.db[i] + k4.db[i])),
  };
}

export interface Simulation {
  step(): StepResult;
  getState(): SimState;
  reset(state: SimState): void;
  getNodes(): Array<{ x: number; y: number }>;
}

export function createSimulation(
  system: SystemType,
  params: PendulumParams,
  sim: SimSettings,
  initialState: SimState,
): Simulation {
  const dt = sim.dt;
  const p = params;
  const n = Math.max(1, Math.min(4, Math.floor(p.sculptureN)));

  let state = initialState.slice();

  function getNodes(): Array<{ x: number; y: number }> {
    if (system === 'rigid' || system === 'elastic12' || system === 'nonlinear') {
      const t1 = state[0];
      const w1 = state[1];
      const t2 = state[4];
      const l1 = system === 'rigid' ? p.L1 : p.L1 + state[2];
      const l2 = system === 'rigid' ? p.L2 : p.L2 + state[6];
      return [bob1Position(t1, l1), bob2Position(t1, t2, l1, l2)];
    }
    if (system === 'sculpture') {
      return sculptureNodes(state[0], state[2], state[4], state[6], p.sculptureWeight, p.sculptureRod, p.sculptureAxle, p.sculptureReduction, n);
    }
    return resonantNodes(state[0], state[4], p.rpM0, p.rpM1, p.rpL0, p.rpL1, p.rpA0);
  }

  function step(): StepResult {
    if (system === 'rigid') {
      const rs: RigidState = {
        theta1: state[0], omega1: state[1], theta2: state[4], omega2: state[5],
      };
      const next = rigidStepVerlet(rs, dt, p.m1, p.m2, p.L1, p.L2);
      state = pack8(next.theta1, next.omega1, 0, 0, next.theta2, next.omega2, 0, 0);
    } else if (system === 'elastic12') {
      const sa = state.slice(0, 4);
      const sb = state.slice(4, 8);
      const next = rk4Step(sa, sb,
        (a, b) => elasticDerivatives(a, b, p.m1, p.m2, p.L1, p.L2, p.k1, p.k2),
        dt,
      );
      state = [...next.sa, ...next.sb];
    } else if (system === 'nonlinear') {
      const sa = state.slice(0, 4);
      const sb = state.slice(4, 8);
      const next = rk4Step(sa, sb,
        (a, b) => nonlinearDerivatives(a, b, p.m1, p.m2, p.L1, p.L2, p.k1, p.k2),
        dt,
      );
      state = [...next.sa, ...next.sb];
    } else if (system === 'sculpture') {
      const sa = state.slice(0, 4);
      const sb = state.slice(4, 8);
      const next = rk4Step(sa, sb,
        (a, b) => sculptureDerivatives(a, b, p.sculptureWeight, p.sculptureRod, p.sculptureAxle, p.sculptureReduction, n),
        dt,
      );
      state = [...next.sa, ...next.sb];
    } else {
      const sa = state.slice(0, 4);
      const sb = state.slice(4, 8);
      const next = rk4Step(sa, sb,
        (a, b) => {
          const d = resonantDerivatives(a[0], a[1], b[0], b[1], p.rpM0, p.rpM1, p.rpL0, p.rpL1, p.rpA0);
          return { da: [d.dTheta0, d.dOmega0, 0, 0], db: [d.dTheta1, d.dOmega1, 0, 0] };
        },
        dt,
      );
      state = [...next.sa, ...next.sb];
    }
    return { state: state.slice(), nodes: getNodes() };
  }

  function getState(): SimState { return state; }
  function reset(s: SimState): void { state = s.slice(); }

  return { step, getState, reset, getNodes };
}
